/**
 * Patient360DynamicZone.tsx — Renders all resolved sections belonging to a zone.
 * Filters and sorts sections by zone key and priority score.
 */

import { memo } from 'react';
import type { Patient360ResolvedSection } from '../../types/dynamicEntityView';
import { Patient360SmartSection } from './Patient360SmartSection';

interface Patient360DynamicZoneProps {
  zoneKey: string;
  sections: Patient360ResolvedSection[];
  programId?: string;
  patientId?: string;
  columns?: 1 | 2 | 3;
  className?: string;
}

export const Patient360DynamicZone = memo(function Patient360DynamicZone({
  zoneKey,
  sections,
  programId,
  patientId,
  columns = 2,
  className,
}: Patient360DynamicZoneProps) {
  // Filter sections for this zone and sort by priorityScore DESC
  const zoneSections = sections
    .filter(s => s.zone === zoneKey)
    .sort((a, b) => b.priorityScore - a.priorityScore || a.configuredOrder - b.configuredOrder);

  if (zoneSections.length === 0) return null;

  return (
    <div className={className ?? 'space-y-3'}>
      {zoneSections.map(section => (
        <Patient360SmartSection
          key={section.sectionKey}
          sectionKey={section.sectionKey}
          title={section.title}
          fields={section.fields}
          badges={section.badges}
          collapsible={section.collapsible}
          defaultExpanded={section.defaultExpanded}
          isEmpty={section.isEmpty}
          priorityScore={section.priorityScore}
          programId={programId}
          patientId={patientId}
          columns={columns}
        />
      ))}
    </div>
  );
});
